<template>
  <div>
    <v-alert v-if="loadFailed" type="error" text dense class="mb-4">
      We couldn't load the mailboxes on this account.
      <gg-btn small tertiary class="ml-2" @click="load">Try again</gg-btn>
    </v-alert>

    <gg-loading v-if="loading && !mailboxOptions.length" :loading="true" type="card" />

    <!-- Every panel here needs a mailbox, so with none the tab offers to
         create one (AM-1107). -->
    <feature-setup
      v-else-if="!mailboxOptions.length && !loadFailed"
      feature="accounts"
      @create="$emit('create-mailbox')"
    />

    <template v-else-if="mailboxOptions.length">
      <v-row class="mb-2">
        <v-col cols="12" sm="8" md="5">
          <gg-select
            :value="mailbox && mailbox.email"
            label="Mailbox"
            :items="mailboxOptions"
            hide-details
            @change="select"
          />
        </v-col>
      </v-row>

      <gg-card v-if="!mailbox">
        <gg-card-text>
          <div class="py-8 text-center">
            <p class="mb-2 font-weight-medium">We couldn't find this mailbox.</p>
            <p class="mb-0">It may have been deleted. Pick another one above.</p>
          </div>
        </gg-card-text>
      </gg-card>

      <!-- Keyed by mailbox so switching mailboxes starts the panel afresh
           rather than carrying the previous mailbox's loaded state over. -->
      <component
        :is="panel"
        v-else
        :key="mailbox.id"
        :mailbox="mailbox"
        @changed="reload"
      />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MailboxAliasesView from '@/views/email/mailbox/MailboxAliasesView'
import MailboxClientSetupView from '@/views/email/mailbox/MailboxClientSetupView'
import MailboxSecurityView from '@/views/email/mailbox/MailboxSecurityView'
import FeatureSetup from '@/components/Email/FeatureSetup'
import { accountPath } from '@/consts/email'

// Route section -> the per-mailbox panel it shows.
const PANELS = {
  aliases: MailboxAliasesView,
  'client-setup': MailboxClientSetupView,
  security: MailboxSecurityView,
}

/**
 * An account tab that works on one mailbox at a time: Aliases, Client Setup and
 * Security. These used to be tabs of a separate mailbox page, which was dropped
 * (AM-1105), so the mailbox is now picked here and kept in the URL as
 * `<section>/<mailbox>` — the same shape as `filters/:account`.
 */
export default {
  name: 'AccountMailboxSectionView',
  components: { FeatureSetup },
  // Passed down by EmailAccountView.
  props: {
    service: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      loading: false,
      loadFailed: false,
    }
  },
  computed: {
    ...mapGetters('emailHub', ['scopedMailboxes', 'mailboxFor']),
    section () {
      return this.$route.meta.mailboxSection
    },
    panel () {
      return PANELS[this.section]
    },
    // Only the mailboxes of the domain this account is opened on: the main
    // domain or one of its addon domains.
    mailboxOptions () {
      return this.scopedMailboxes(this.service.id, this.$route.params.maindomain)
        .map(mailbox => mailbox.email)
        .sort()
    },
    // vue-router has already decoded this, so it is the plain address.
    address () {
      return this.$route.params.mailbox || ''
    },
    mailbox () {
      return this.address ? this.mailboxFor(this.service.id, this.address) : null
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadServiceMailboxes']),
    sectionPath (email = '') {
      return accountPath(this.$route.params.maindomain, this.section, email)
    },
    select (email) {
      if (email && email !== this.address) {
        this.$router.push(this.sectionPath(email))
      }
    },
    /**
     * With no mailbox in the URL, open the first one so the tab is never an
     * empty picker. `replace` keeps the bare section out of history, so Back
     * does not bounce straight into the same redirect.
     */
    pickDefault () {
      if (!this.address && this.mailboxOptions.length) {
        this.$router.replace(this.sectionPath(this.mailboxOptions[0]))
      }
    },
    /**
     * Re-read on every visit rather than trusting the store: the hosting Email
     * section edits the same mailboxes and does not tell this module. A mailbox
     * already on screen stays rendered while that happens.
     */
    async load () {
      this.loading = true
      this.loadFailed = false
      try {
        await this.loadServiceMailboxes(this.service)
      } catch (e) {
        this.loadFailed = true
      } finally {
        this.loading = false
      }
      this.pickDefault()
    },
    // A change made in a panel (a restriction, an alias) re-reads the service,
    // which is what moves the mailbox's status and alias count elsewhere.
    reload () {
      return this.load()
    },
  },
  watch: {
    // Clicking the tab itself while a mailbox is open lands on the bare section.
    address () {
      this.pickDefault()
    },
  },
  created () {
    this.pickDefault()
    this.load()
  },
}
</script>
